import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { createCartMessageHandler, getCart } from "@theme/utilities";

/**
 * A custom element that displays a sticky mobile cart button.
 * Opens the cart drawer on click and syncs with cart count.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartCount - The cart count element.
 *
 * @extends {Component<Refs>}
 */
class MobileStickyCartButton extends Component {
  requiredRefs = ["cartCount"];

  /** @type {number} */
  get currentCartCount() {
    const text = this.refs.cartCount?.textContent?.trim() || "";
    const match = text.match(/(\d+)/);
    return match && match[1] ? parseInt(match[1], 10) : 0;
  }

  set currentCartCount(value) {
    if (!this.refs.cartCount) return;
    const itemText = value === 1 ? "Item" : "Items";
    this.refs.cartCount.textContent = `${value} ${itemText}`;
  }

  connectedCallback() {
    super.connectedCallback();

    // Listen for cart update events
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);

    // Listen for cart quantity change events from cart drawer
    const { cleanup: cleanupCartMessage } = createCartMessageHandler({
      onQuantityChange: this.onCartQuantityChange,
      logPrefix: "Mobile sticky cart button",
    });
    this.#cartMessageCleanup = cleanupCartMessage;

    // Sync cart count on load - this handles showing/hiding and image update
    this.ensureCartCountIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    if (this.#cartMessageCleanup) {
      this.#cartMessageCleanup();
    }

    // Clear debounce timer
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }
  }

  /** @type {(() => void) | null} */
  #cartMessageCleanup = null;

  /** @type {number | null} */
  #lastApiFetchTime = null;

  /** @type {number | null} */
  #debounceTimer = null;

  /**
   * Debounced API fetch to prevent rapid successive calls
   */
  #debouncedFetchCart = async () => {
    // Clear existing timer
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }

    // If we fetched recently (< 500ms), debounce
    const now = Date.now();
    if (this.#lastApiFetchTime && now - this.#lastApiFetchTime < 500) {
      this.#debounceTimer = setTimeout(() => {
        this.#fetchCartAndUpdate();
      }, 500);
      return;
    }

    // Fetch immediately
    await this.#fetchCartAndUpdate();
  };

  /**
   * Fetches cart from API and updates count/image
   */
  #fetchCartAndUpdate = async () => {
    this.#lastApiFetchTime = Date.now();
    
    try {
      const cart = await getCart();
      const actualItemCount = cart.item_count ?? 0;
      
      await this.renderCartCount(actualItemCount);
      
      // Update product image if cart has items
      if (actualItemCount > 0) {
        await this.updateProductImage();
      } else {
        this.hideProductImage();
      }
    } catch (error) {
      console.error("Mobile sticky cart: failed to fetch cart count:", error);
    }
  };

  /**
   * Handles cart quantity changes from cart drawer message events.
   * This is reliable - cart drawer provides total_cart_quantity directly.
   * @param {{ total_cart_quantity: number }} data
   */
  onCartQuantityChange = async ({ total_cart_quantity }) => {
    // Trust this value - it comes directly from cart drawer
    await this.renderCartCount(total_cart_quantity);
    
    // Update product image if cart has items
    if (total_cart_quantity > 0) {
      await this.updateProductImage();
    } else {
      this.hideProductImage();
    }
  };

  /**
   * Handles the cart update event.
   * Optimized to only fetch API when necessary.
   * @param {CustomEvent} event
   */
  onCartUpdate = async (event) => {
    const eventData = event.detail?.data;
    
    // If event has error, don't update
    if (eventData?.didError) {
      return;
    }

    // Skip if this event is from cart drawer (we handle it via onCartQuantityChange)
    if (eventData?.source === "cart-drawer-message") {
      return;
    }

    const itemCount = eventData?.itemCount;
    const source = eventData?.source;

    // Trust itemCount if it's from reliable sources or if it's a number
    // Reliable sources: cart-drawer-message (handled separately), product-quantity-selector
    const isReliableSource = source === "product-quantity-selector" || source === "product-sticky-bar";
    
    if (source === "product-form-component" && typeof itemCount === "number") {
      const nextCount = Math.max(this.currentCartCount + itemCount, itemCount);
      await this.renderCartCount(nextCount);
      
      if (nextCount > 0) {
        await this.updateProductImage();
      } else {
        this.hideProductImage();
      }
      return;
    }

    if (typeof itemCount === "number" && isReliableSource) {
      // Trust the count from reliable sources
      await this.renderCartCount(itemCount);
      
      if (itemCount > 0) {
        await this.updateProductImage();
      } else {
        this.hideProductImage();
      }
      return;
    }

    // For other sources or missing itemCount, fetch from API (debounced)
    // This handles cases where itemCount might be a delta or missing
    await this.#debouncedFetchCart();
  };

  /**
   * Renders the cart count and shows/hides the button.
   * @param {number} itemCount
   */
  renderCartCount = async (itemCount) => {
    this.currentCartCount = itemCount;

    // Show/hide button based on cart count
    if (this.currentCartCount === 0) {
      this.hideButton();
    } else {
      this.showButton();
    }

    // Store in sessionStorage
    sessionStorage.setItem(
      "cart-count",
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );
  };

  /**
   * Hides the button with animation
   */
  hideButton = () => {
    const wasVisible = !this.classList.contains("hidden");
    if (wasVisible) {
      this.classList.add("mobile-sticky-cart-button--hiding");
      setTimeout(() => {
        this.classList.add("hidden");
        this.classList.remove("mobile-sticky-cart-button--hiding");
      }, 300);
    } else {
      this.classList.add("hidden");
    }
  };

  /**
   * Shows the button with animation
   */
  showButton = () => {
    const wasHidden = this.classList.contains("hidden");
    this.classList.remove("hidden");
    this.classList.remove("mobile-sticky-cart-button--hiding");
    
    if (wasHidden) {
      setTimeout(() => {
        void this.offsetHeight;
      }, 0);
    }
  };

  /**
   * Syncs cart count on load.
   * Simple: Only hide if count is 0, otherwise show.
   */
  ensureCartCountIsCorrect = async () => {
    const visibleCount = this.refs.cartCount?.textContent?.trim() || "";
    const match = visibleCount.match(/(\d+)/);
    const currentVisibleCount = match ? parseInt(match[1], 10) : 0;

    // Use visible count from DOM (server-rendered) - it's the most reliable on initial load
    if (currentVisibleCount > 0) {
      // Cart has items - ensure button is visible
      this.currentCartCount = currentVisibleCount;
      this.showButton();
      
      // Update product image
      await this.updateProductImage();
      
      // Store in sessionStorage
      sessionStorage.setItem(
        "cart-count",
        JSON.stringify({
          value: String(currentVisibleCount),
          timestamp: Date.now(),
        })
      );
    } else {
      // Cart is empty - hide button
      this.currentCartCount = 0;
      this.hideButton();
    }
  };

  /**
   * Updates the product image directly from a URL (optimized path)
   * Uses the same optimized image URL format as animateFlyToCart
   * @param {string} imageUrl
   */
  updateProductImageFromUrl = (imageUrl) => {
    const productImage = /** @type {HTMLImageElement | null} */ (this.querySelector(".mobile-sticky-cart-button__product-image"));
    const fallbackIcon = /** @type {HTMLElement | null} */ (this.querySelector(".mobile-sticky-cart-button__fallback-icon"));

    if (!productImage || !imageUrl) return;

    // Preload image for faster display
    const img = new Image();
    img.onload = () => {
      productImage.src = imageUrl;
      productImage.style.display = "block";
      if (fallbackIcon) {
        fallbackIcon.style.display = "none";
      }
    };
    img.onerror = () => {
      // Fallback to cart API if image fails to load
      this.updateProductImage();
    };
    img.src = imageUrl;
  };

  /**
   * Updates the product image from cart API (fallback method)
   * Only updates existing image element, doesn't create new ones
   */
  updateProductImage = async () => {
    const productImage = /** @type {HTMLImageElement | null} */ (this.querySelector(".mobile-sticky-cart-button__product-image"));
    const fallbackIcon = /** @type {HTMLElement | null} */ (this.querySelector(".mobile-sticky-cart-button__fallback-icon"));

    if (!productImage) return; // Only update if image element exists (server-side rendered)

    try {
      const cart = await getCart();

      if (cart.items && cart.items.length > 0 && cart.items[0].image) {
        const imageSize = parseInt(getComputedStyle(this).getPropertyValue("--product-image-size") || "28", 10);
        let imageUrl = cart.items[0].image;

        // Optimize image URL using Shopify's width parameter (same format as animateFlyToCart)
        // Use &width= parameter for better optimization
        if (imageUrl && !imageUrl.includes("&width=")) {
          imageUrl = imageUrl.split("?")[0] + `?width=${imageSize * 2}`; // 2x for retina
        } else if (imageUrl && imageUrl.includes("&width=")) {
          // Update existing width parameter
          imageUrl = imageUrl.replace(/&width=\d+/, `&width=${imageSize * 2}`);
        }

        const imageAlt = cart.items[0].product_title || "";

        // Preload image for faster display
        const img = new Image();
        img.onload = () => {
          productImage.src = imageUrl;
          productImage.alt = imageAlt;
          productImage.style.display = "block";
          if (fallbackIcon) {
            fallbackIcon.style.display = "none";
          }
        };
        img.onerror = () => {
          this.hideProductImage();
        };
        img.src = imageUrl;
      } else {
        // No image available, hide product image and show fallback
        this.hideProductImage();
      }
    } catch (error) {
      console.error("Failed to update cart image:", error);
      // Don't hide image on error, keep current state
    }
  };

  /**
   * Hides product image and shows fallback icon
   */
  hideProductImage = () => {
    const productImage = /** @type {HTMLImageElement | null} */ (this.querySelector(".mobile-sticky-cart-button__product-image"));
    const fallbackIcon = /** @type {HTMLElement | null} */ (this.querySelector(".mobile-sticky-cart-button__fallback-icon"));

    if (productImage) {
      productImage.style.display = "none";
    }

    if (fallbackIcon) {
      fallbackIcon.style.display = "flex";
    }
  };
}

if (!customElements.get("mobile-sticky-cart-button")) {
  customElements.define("mobile-sticky-cart-button", MobileStickyCartButton);
}
